import { Stack, useTheme } from "@mui/material";
import { getDollarValue, formatCurrencyAmount, DiscountType, AvailablePaymentCurrency } from "@simplyk/common";
import { FieldValues, Path, UseFormRegister } from "react-hook-form";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Trans, useTranslate } from "@/hooks/useTranslate";
import { Success } from "@/icons/outlined";

type ValidDiscountCodeBlockProps<T extends FieldValues> = {
  register: UseFormRegister<T>;
  name: Path<T>;
  discountType?: DiscountType;
  discountCode?: string;
  discountAmount?: number;
  currency: AvailablePaymentCurrency;
  onRemoveClick: () => void;
};

export function ValidDiscountCodeBlock<T extends FieldValues>({
  register,
  name,
  currency,
  discountType,
  discountCode,
  discountAmount,
  onRemoveClick,
}: ValidDiscountCodeBlockProps<T>) {
  const theme = useTheme();
  const { isoLocale } = useLocaleContext();
  const { t } = useTranslate();

  const getValidDiscountLabel = (): string => {
    if (!discountCode) {
      return t("ticketing", "discountCodeField.valid.defaultLabel");
    }

    return discountType === DiscountType.Dollars
      ? formatCurrencyAmount({
          value: getDollarValue(discountAmount ?? 0),
          locale: isoLocale,
          currency,
        })
      : `${discountAmount}%`;
  };

  return (
    <Stack
      direction={{ sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      gap={2}
      sx={{
        padding: 2,
        color: theme.palette.formsComponents.paymentSummary.textColor,
        backgroundColor: theme.palette.formsComponents.paymentSummary.backgroundColor,
        border: `1px solid ${theme.palette.formsComponents.paymentSummary.borderColor}`,
        borderRadius: theme.radius(1),
      }}
    >
      <input type="hidden" {...register(name)} />
      <Stack direction="row" alignItems="center" gap={2} flexGrow={{ sm: 1 }}>
        <FormIcon sx={{ display: { xs: "none", sm: "block" } }}>
          <Success />
        </FormIcon>
        <FormTypography variant="body2" sx={{ flexGrow: 1 }}>
          <Trans
            space="ticketing"
            i18nKey="validDiscountCodeApplied"
            components={{ emphasis: <FormTypography variant="subtitle2" component="span" /> }}
            values={{
              discountCode,
              discountValue: getValidDiscountLabel(),
            }}
          />
        </FormTypography>
      </Stack>
      <FormButton size="small" variant="outline" onClick={onRemoveClick}>
        {t("common", "remove")}
      </FormButton>
    </Stack>
  );
}
